import type {LocalizationConfig} from '@cohort/shared/schema/common';
import {EmailSchema} from '@cohort/shared/schema/common';
import type {CohortFormConfig, CohortFormQuestion} from '@cohort/shared/schema/common/cohortForm';
import {
  buildLocalizationConfig,
  formatI18nLanguage,
  getLocalizedContentOrThrow,
} from '@cohort/shared/utils/localization';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import PrimaryButton from '@cohort/wallet/components/button/PrimaryButton';
import type {SelectOption} from '@cohort/wallet/components/forms/DropDownInput';
import {DropDownInput} from '@cohort/wallet/components/forms/DropDownInput';
import {Input} from '@cohort/wallet/components/forms/Input';
import {Transition} from '@cohort/wallet/components/transitions/Transition';
import {useMerchantContext} from '@cohort/wallet/hooks/useMerchantContext';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import type {TrackingConfig} from '@cohort/wallet/lib/Tracking';
import i18n from 'i18next';
import React, {Fragment, useCallback, useState} from 'react';
import type {UseFormRegister} from 'react-hook-form';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {match} from 'ts-pattern';
import {z} from 'zod';

type QuestionInputProps = {
  question: CohortFormQuestion;
  index: number;
  localizationConfig: LocalizationConfig;
  formErrors: Record<string, string>;
  getValues: (name: string) => string | undefined;
  setValue: (name: string, value: string) => void;
  register: UseFormRegister<Record<string, string | undefined>>;
};

const QuestionInput: React.FC<QuestionInputProps> = ({
  question,
  index,
  localizationConfig,
  formErrors,
  getValues,
  setValue,
  register,
}) => {
  const {t} = useTranslation('components', {keyPrefix: 'forms.cohortForm'});

  const localizedOptions =
    question.options?.map(option => {
      return {
        value: option.value,
        label: getLocalizedContentOrThrow(option.label, localizationConfig),
      };
    }) ?? null;
  const localizedName = getLocalizedContentOrThrow(question.name, localizationConfig);
  const {accentColor, hasDarkBg} = useThemeContext();

  const renderError = useCallback(
    (questionId: string): JSX.Element => {
      if (formErrors[questionId] === undefined) {
        return <Fragment />;
      }

      return (
        <Transition appear enterFrom="bottom" show twClasses="text-red-500 text-sm mt-1">
          <Fragment>{formErrors[questionId]}</Fragment>
        </Transition>
      );
    },
    [formErrors]
  );

  return match(question.type)
    .with('text', () => (
      <div className="flex flex-col">
        <label className="mb-1 text-sm">
          {localizedName}
          {question.mandatory && <span>*</span>}
        </label>

        <Input
          testId={`perk-form-input-${index}`}
          name="perk-usage-input"
          type="text"
          accentColor={accentColor}
          hasDarkBg={hasDarkBg}
          required={question.mandatory}
          register={register(question.id)}
        />
        {renderError(question.id)}
      </div>
    ))
    .with('date', () => {
      return (
        <div className="flex flex-col">
          <label className="mb-1 text-sm">
            {localizedName}
            {question.mandatory && <span>*</span>}
          </label>

          <Input
            testId={`perk-form-input-${index}`}
            name="perk-usage-input"
            type="date"
            accentColor={accentColor}
            hasDarkBg={hasDarkBg}
            required={question.mandatory}
            register={register(question.id, {
              setValueAs: (value: unknown) => {
                const parsed = z.coerce.date().safeParse(value);
                if (!parsed.success) {
                  return '';
                }
                return parsed.data;
              },
            })}
          />
          {renderError(question.id)}
        </div>
      );
    })
    .with('number', () => (
      <div className="flex flex-col">
        <label className="mb-1 text-sm">
          {localizedName}
          {question.mandatory && <span>*</span>}
        </label>

        <Input
          testId={`perk-form-input-${index}`}
          name="perk-usage-input"
          type="number"
          accentColor={accentColor}
          hasDarkBg={hasDarkBg}
          required={question.mandatory}
          register={register(question.id)}
        />
        {renderError(question.id)}
      </div>
    ))
    .with('select', () => {
      if (localizedOptions === null) {
        throw new Error('No options for select');
      }
      return (
        <Fragment>
          <DropDownInput
            label={`${localizedName} ${question.mandatory ? '*' : ''}`}
            value={{
              value: getValues(question.id) ?? '',
              label: getValues(question.id) ?? t('dropdownDefaultValue'),
            }}
            options={localizedOptions}
            onChange={(selectedValue: SelectOption) => setValue(question.id, selectedValue.value)}
          />
          {renderError(question.id)}
        </Fragment>
      );
    })
    .with('checkbox', () => (
      <Fragment>
        <label className="cursor-pointer">
          <input
            data-testid={`perk-form-checkbox-${index}`}
            type="checkbox"
            {...register(question.id)}
            className={classNames(
              'h-4 w-4 cursor-pointer rounded border focus:outline-none focus:ring-0 focus:ring-offset-0',
              hasDarkBg ? 'border-white/10 bg-white/5' : 'border-black/10 bg-black/5'
            )}
            style={{color: accentColor}}
          />
          <span className="ml-2 text-sm">{localizedName}</span>
          {question.mandatory && <span> *</span>}
        </label>
        {renderError(question.id)}
      </Fragment>
    ))
    .with('email', () => (
      <div className="flex flex-col">
        <label className="mb-1 text-sm">
          {localizedName} {question.mandatory && <span>*</span>}
        </label>

        <Input
          testId={`perk-form-email-input-${index}`}
          name="perk-usage-email-input"
          type="email"
          accentColor={accentColor}
          hasDarkBg={hasDarkBg}
          required={question.mandatory}
          register={register(question.id)}
        />
        {renderError(question.id)}
      </div>
    ))
    .exhaustive();
};

type CohortFormComponentProps = {
  config: CohortFormConfig;
  onSubmit: (values: Record<string, string | undefined>) => void;
  isLoading: boolean;
  onSubmitTracking: TrackingConfig;
};

export const CohortFormComponent: React.FC<CohortFormComponentProps> = ({
  config,
  onSubmit,
  isLoading,
  onSubmitTracking,
}) => {
  const merchant = useMerchantContext();
  const {t} = useTranslation('components', {keyPrefix: 'forms.cohortForm'});
  const {register, handleSubmit: onFormSubmit, setValue, getValues} = useForm();
  const {hasDarkBg} = useThemeContext();

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const localizationConfig = buildLocalizationConfig(
    formatI18nLanguage(i18n.language),
    merchant.supportedLanguages,
    merchant.defaultLanguage
  );

  const validateForm = (data: Record<string, string | undefined>): void => {
    const values: Record<string, string | undefined> = {};
    const submitErrors: Record<string, string> = {};
    for (const question of config.questions) {
      values[question.id] = data[question.id];

      if (question.mandatory && (data[question.id] ?? '').length === 0) {
        submitErrors[question.id] = t('required');
      }

      if (question.type === 'email' && (data[question.id] ?? '').length > 0) {
        try {
          EmailSchema.parse(data[question.id] ?? '');
        } catch {
          submitErrors[question.id] = t('invalidEmail');
        }
      }

      if (question.type === 'checkbox' && question.mandatory && !data[question.id]) {
        submitErrors[question.id] = t('checkboxRequired');
      }
    }

    if (Object.keys(submitErrors).length > 0) {
      setFormErrors(submitErrors);
      return;
    }

    onSubmit(values);
  };

  return (
    <Fragment>
      <div
        dangerouslySetInnerHTML={{
          __html: getLocalizedContentOrThrow(config.description, localizationConfig),
        }}
        className={classNames('prose mt-2', hasDarkBg && 'prose-invert')}
      />
      <form className="mt-3 max-w-3xl space-y-5 px-[1px]">
        {config.questions.map((question, index) => (
          <QuestionInput
            question={question}
            index={index}
            localizationConfig={localizationConfig}
            key={question.id}
            formErrors={formErrors}
            getValues={getValues}
            setValue={setValue}
            register={register}
          />
        ))}
        <PrimaryButton
          testId="perk-form-submit"
          text={t('submit')}
          className="w-full"
          onClick={onFormSubmit(validateForm)}
          isLoading={isLoading}
          tracking={onSubmitTracking}
        />
      </form>
    </Fragment>
  );
};
export default CohortFormComponent;
